import { FC, useCallback, useEffect, useState } from 'react'
import {
  AutocompleteInput,
  Button,
  Confirm,
  required,
  SaveButton,
  SimpleForm,
  TextInput,
  Toolbar,
  useLocale,
  useRecordContext,
  useSaveContext,
  useTranslate,
} from 'react-admin'
import { useFormContext, useFormState, useWatch } from 'react-hook-form'
import SaveIcon from '@mui/icons-material/Save'
import { Alert, Typography } from '@mui/material'
import { useApiGet } from '../../hooks/useApiGet'
import type {
  OrganizationApp,
  OrganizationAppOption,
  OrganizationAuth0Option,
  OrganizationRecord,
} from '../../types/apiTypes'

type Apps = Record<string, string | null | undefined> | undefined

// Form values -> API map. Empty selection becomes null (= remove link) on update, and is omitted on create.
export const cleanApps = (apps: Apps, keepEmpty: boolean): Record<string, string | null> => {
  const result: Record<string, string | null> = {}
  Object.entries(apps ?? {}).forEach(([key, value]) => {
    // eslint-disable-next-line security/detect-object-injection
    if (value) result[key] = value
    // eslint-disable-next-line security/detect-object-injection
    else if (keepEmpty) result[key] = null
  })
  return result
}

// "Gemeente Utrecht" and "utrecht" are the same name for the mismatch warning.
const normalise = (name: string): string => name.trim().toLowerCase().replace(/^gemeente\s+/, '')

// Create: confirm before saving because the Auth0 id cannot be changed afterwards.
const OrganizationToolbar: FC<{ blocked: boolean; confirm: boolean }> = ({ blocked, confirm }) => {
  const { save, saving } = useSaveContext()
  const { handleSubmit, trigger } = useFormContext()
  const [open, setOpen] = useState(false)

  if (!confirm) {
    return (
      <Toolbar>
        <SaveButton disabled={blocked || saving} />
      </Toolbar>
    )
  }

  return (
    <Toolbar>
      <Button
        label="ra.action.save"
        variant="contained"
        disabled={blocked || saving}
        onClick={() => {
          // Validate first so the dialog is not shown for an invalid form.
          void trigger().then(valid => valid && setOpen(true))
        }}
      >
        <SaveIcon />
      </Button>
      <Confirm
        isOpen={open}
        title="resources.organization.confirmTitle"
        content="resources.organization.confirmContent"
        onConfirm={() => {
          setOpen(false)
          void handleSubmit(values => save?.(values))()
        }}
        onClose={() => setOpen(false)}
      />
    </Toolbar>
  )
}

// Prefill name from the chosen Auth0 organization, but never overwrite what the user typed.
const NamePrefill: FC<{ options?: OrganizationAuth0Option[] }> = ({ options }) => {
  const { setValue } = useFormContext()
  const { dirtyFields } = useFormState()
  const organizationId = useWatch({ name: 'organizationId' }) as string | undefined

  useEffect(() => {
    const auth0Name = options?.find(option => option.id === organizationId)?.name
    if (auth0Name && !dirtyFields.name) setValue('name', auth0Name)
  }, [organizationId, options])

  return null
}

interface AppFieldProps {
  app: OrganizationApp
  exclude: string
  auth0Options?: OrganizationAuth0Option[]
  onError: (key: string, failed: boolean) => void
}

// One optional link per app in the registry; options come from the app's provider.
const AppField: FC<AppFieldProps> = ({ app, exclude, auth0Options, onError }) => {
  const translate = useTranslate()
  const locale = useLocale()
  const record = useRecordContext<OrganizationRecord>()
  const { data, error } = useApiGet<OrganizationAppOption[]>(
    `/organization/app_options/${encodeURIComponent(app.key)}${exclude}`
  )
  const organizationId = useWatch({ name: 'organizationId' }) as string | undefined
  const value = useWatch({ name: `apps.${app.key}` }) as string | undefined

  useEffect(() => onError(app.key, error), [app.key, error, onError])

  const current = record?.apps?.[app.key]
  const choices = (data ?? []).map(option => ({ ...option, disabled: !!option.inUseBy }))
  // Keep the saved value visible even when the provider no longer returns it.
  if (current && !choices.some(choice => choice.id === current)) {
    choices.push({
      id: current,
      externalId: current,
      name: record?.appNames?.[app.key] || current,
      hint: null,
      inUseBy: null,
      disabled: false,
    })
  }

  const optionText = (option?: OrganizationAppOption): string => {
    if (!option) return ''
    const hint = option.hint ? ` (${option.hint})` : ''
    const inUse = option.inUseBy ? ` - ${translate('resources.organization.inUseBy', { name: option.inUseBy })}` : ''
    return `${option.name} — ${option.externalId}${hint}${inUse}`
  }

  // Certificates were issued for this link: changing it would redirect installs to another customer.
  const locked = !!record?.hasCertificates && !!current
  const appName = choices.find(choice => choice.id === value)?.name
  const auth0Name = auth0Options?.find(option => option.id === (organizationId ?? record?.organizationId))?.name
  const mismatch = !!appName && !!auth0Name && normalise(appName) !== normalise(auth0Name)
  const label = translate(`resources.organization.apps.${app.key}`, {
    _: locale === 'en' ? app.label.en : app.label.nl,
  })

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{translate('resources.organization.sourceUnavailable', { source: label })}</Alert>}
      <AutocompleteInput
        source={`apps.${app.key}`}
        label={label}
        choices={choices}
        optionText={optionText}
        optionValue="id"
        readOnly={locked}
        disabled={error}
        helperText={locked ? 'resources.organization.locked' : false}
        fullWidth
      />
      {mismatch && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {translate('resources.organization.nameMismatch', { appName, auth0Name })}
        </Alert>
      )}
    </>
  )
}

const OrganizationForm: FC<{ isEdit?: boolean }> = ({ isEdit = false }) => {
  const translate = useTranslate()
  const record = useRecordContext<OrganizationRecord>()
  // Edit: exclude this organization so its own links are not shown as "in use".
  const exclude = isEdit && record?.id ? `?excludeOrganization=${encodeURIComponent(record.id)}` : ''
  const auth0 = useApiGet<OrganizationAuth0Option[]>(`/organization/auth0_options${exclude}`)
  const apps = useApiGet<OrganizationApp[]>('/organization/apps')
  const [failed, setFailed] = useState<Record<string, boolean>>({})
  const onError = useCallback((key: string, isFailed: boolean) => {
    // eslint-disable-next-line security/detect-object-injection
    setFailed(previous => (previous[key] === isFailed ? previous : { ...previous, [key]: isFailed }))
  }, [])

  // Existing organizations stay editable when only the Auth0 source is down; the Auth0 id is not edited there.
  const auth0Unavailable = !isEdit && auth0.error
  const fieldFailed = Object.values(failed).some(Boolean)
  const blocked = auth0Unavailable || apps.error || fieldFailed

  const auth0Choices = (auth0.data ?? []).map(option => ({ ...option, disabled: !!option.inUseBy }))
  const auth0Text = (option?: OrganizationAuth0Option): string => {
    if (!option) return ''
    const inUse = option.inUseBy ? ` - ${translate('resources.organization.inUseBy', { name: option.inUseBy })}` : ''
    return `${option.name} — ${option.id}${inUse}`
  }

  return (
    <SimpleForm toolbar={<OrganizationToolbar blocked={blocked} confirm={!isEdit} />}>
      {((!isEdit && auth0.error) || apps.error) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate('resources.organization.sourceUnavailable', {
            source: !isEdit && auth0.error ? 'Auth0' : translate('resources.organization.fields.apps'),
          })}
        </Alert>
      )}
      {isEdit ? (
        <TextInput source="organizationId" label="resources.organization.fields.organizationId" readOnly fullWidth />
      ) : (
        <>
          <AutocompleteInput
            source="organizationId"
            label="resources.organization.fields.auth0"
            choices={auth0Choices}
            optionText={auth0Text}
            optionValue="id"
            validate={required()}
            disabled={auth0.error}
            fullWidth
          />
          <Alert severity="info" sx={{ mb: 2 }}>{translate('resources.organization.authImmutable')}</Alert>
          <NamePrefill options={auth0.data} />
        </>
      )}
      <TextInput source="name" label="resources.organization.fields.name" validate={required()} fullWidth />
      {!!apps.data?.length && (
        <Typography variant="subtitle1">{translate('resources.organization.fields.appLinks')}</Typography>
      )}
      {apps.data?.map(app => (
        <AppField key={app.key} app={app} exclude={exclude} auth0Options={auth0.data} onError={onError} />
      ))}
    </SimpleForm>
  )
}

export default OrganizationForm
